/**
 * useTerminalConsole Hook - Web Terminal Console Enterprise
 * Hook enterprise per terminale web sicuro con gestione comandi
 */

import { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS, ApiError, apiFetchJson } from "@/lib/api";

export interface TerminalSession {
    id: string;
    name: string;
    status: "active" | "inactive" | "closed";
    created_at: string;
    last_activity: string;
    command_count: number;
}

export interface CommandHistory {
    id: string;
    command: string;
    output: string;
    exit_code: number;
    executed_at: string;
    duration_ms: number;
}

export interface SystemInfo {
    hostname: string;
    os: string;
    kernel: string;
    uptime: string;
    cpu_cores: number;
    memory_total: string;
    disk_usage: string;
    load_average: number[];
}

export interface ProcessInfo {
    pid: number;
    name: string;
    cpu_percent: number;
    memory_percent: number;
    status: string;
    user: string;
}

export function useTerminalConsole() {
    const [sessions, setSessions] = useState<TerminalSession[]>([]);
    const [activeSession, setActiveSession] = useState<TerminalSession | null>(
        null,
    );
    const [commandHistory, setCommandHistory] = useState<CommandHistory[]>([]);
    const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
    const [processes, setProcesses] = useState<ProcessInfo[]>([]);
    const [currentOutput, setCurrentOutput] = useState<string>("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSessions = useCallback(async () => {
        try {
            setError(null);
            const data = await apiFetchJson<any>(API_ENDPOINTS.TERMINAL_SESSIONS);
            setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
        } catch (err) {
            console.error("Error fetching terminal sessions:", err);
            setError(err instanceof Error ? err.message : "Unknown error");
            setSessions([]);
        }
    }, []);

    const fetchSystemInfo = useCallback(async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.SYSTEM_INFO);
            setSystemInfo(data || null);
        } catch (err) {
            console.error("Error fetching system info:", err);
            setSystemInfo(null);
            setError(err instanceof Error ? err.message : "Unknown error");
        }
    }, []);

    const fetchProcesses = useCallback(async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.SYSTEM_PROCESSES);
            setProcesses(Array.isArray(data?.processes) ? data.processes : []);
        } catch (err) {
            console.error("Error fetching processes:", err);
            setProcesses([]);
            setError(err instanceof Error ? err.message : "Unknown error");
        }
    }, []);

    const executeCommand = useCallback(
        async (command: string, sessionId?: string) => {
            try {
                const data = await apiFetchJson<any>(API_ENDPOINTS.TERMINAL_EXECUTE, {
                    method: "POST",
                    json: {
                        command,
                        session_id: sessionId || activeSession?.id,
                    },
                });

                const historyEntry: CommandHistory = {
                    id: Date.now().toString(),
                    command,
                    output: data?.output || "",
                    exit_code: data?.exit_code || 0,
                    executed_at: new Date().toISOString(),
                    duration_ms: data?.duration_ms || 0,
                };

                setCommandHistory((prev) => [historyEntry, ...prev.slice(0, 49)]); // Keep last 50
                setCurrentOutput(data?.output || "");

                return {
                    success: true,
                    output: data?.output,
                    exit_code: data?.exit_code,
                };
            } catch (err) {
                const errorOutput =
                    err instanceof ApiError
                        ? `Command rejected (HTTP ${err.status}): ${err.detail}`
                        : `Error: ${err instanceof Error ? err.message : "Command execution failed"}`;
                setCurrentOutput(errorOutput);
                setError(errorOutput);

                return {
                    success: false,
                    output: errorOutput,
                    exit_code: 1,
                };
            }
        },
        [activeSession],
    );

    const createSession = useCallback(
        async (name: string) => {
            try {
                const data = await apiFetchJson<any>(API_ENDPOINTS.TERMINAL_SESSIONS, {
                    method: "POST",
                    json: { name },
                });
                await fetchSessions();
                return { success: true, session_id: String(data?.session_id || "") };
            } catch (err) {
                return {
                    success: false,
                    error: err instanceof Error ? err.message : "Unknown error",
                };
            }
        },
        [fetchSessions],
    );

    const closeSession = useCallback(
        async (sessionId: string) => {
            try {
                await apiFetchJson<any>(API_ENDPOINTS.TERMINAL_SESSION_DELETE(sessionId), {
                    method: "DELETE",
                });
                await fetchSessions();
                if (activeSession?.id === sessionId) {
                    setActiveSession(null);
                }
                return { success: true };
            } catch (err) {
                return {
                    success: false,
                    error: err instanceof Error ? err.message : "Unknown error",
                };
            }
        },
        [activeSession, fetchSessions],
    );

    const refresh = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([
            fetchSessions(),
            fetchSystemInfo(),
            fetchProcesses(),
        ]);
        setIsLoading(false);
    }, [fetchSessions, fetchSystemInfo, fetchProcesses]);

    useEffect(() => {
        refresh();

        // Auto-refresh processes every 10 seconds
        const interval = setInterval(fetchProcesses, 10000);
        return () => clearInterval(interval);
    }, [refresh, fetchProcesses]);

    return {
        sessions,
        activeSession,
        commandHistory,
        systemInfo,
        processes,
        currentOutput,
        isLoading,
        error,
        setActiveSession,
        executeCommand,
        createSession,
        closeSession,
        refresh,
    };
}
